import { LoginType, OauthServiceType } from '@websky/graphql';
import { captureErrorSentry } from '../utils';
import { getBackRedirectUrl } from '../SocialAuth/components/utils';
import { SocialAuthParams } from './types';

export const socialAuthPath = '/social-auth';
export const tIdPath = `/t-id`;
export const telegramPath = `/telegram`;
export const vkPath = `/vk`;
export const sberIdPath = `/sber-id`;
export const facebookPath = `/facebook`;
export const googlePath = `/google`;
export const alfaIdPath = `/alfa-id`;

const pathLoginTypeMap: Record<string, string> = {
	[tIdPath]: LoginType.TinkoffID,
	[telegramPath]: LoginType.Telegram,
	[vkPath]: LoginType.Vkontakte,
	[facebookPath]: LoginType.Facebook,
	[sberIdPath]: LoginType.SberID,
	[googlePath]: LoginType.Google,
	[alfaIdPath]: LoginType.AlfaID
};

export const getValuesFromUrl = <T extends string>(urlString: string, parametersList: T[]) => {
	const queryParams: { [key in T]?: string } = {};
	const hashParams: { [key in T]?: string } = {};

	const url = new URL(urlString);

	const queryUrlParams = url.searchParams;
	const hashUrlParams = new URLSearchParams(url.hash.slice(1));

	parametersList.forEach(parameter => {
		const queryValue = queryUrlParams.get(parameter);
		const hashValue = hashUrlParams.get(parameter);

		if (queryValue) {
			queryParams[parameter] = queryValue;
		}

		if (hashValue) {
			hashParams[parameter] = hashValue;
		}
	});

	return {
		queryParams,
		hashParams
	};
};

export const goToMainPage = () => {
	window.location.href = '/';
};

export const parseErrorHandler = (error: string) => {
	captureErrorSentry(error);
	console.error(error);
	goToMainPage();
};

// вспомогательная функция для читаемости ошибок
const getProviderName = (path: string): string => {
	return pathLoginTypeMap[path] || 'Unknown';
};

// вспомогательная функция вывода ошибки, если нет параметров
const parseEmptyParamsErrorHandler = () => {
	parseErrorHandler('Not all parameters are present in the callback url');
};

export const getParsedParams = (locationPathname: string, socialAuthCallbackUrl: string): SocialAuthParams | null => {
	try {
		switch (locationPathname) {
			case tIdPath: {
				const { queryParams } = getValuesFromUrl(window.location.href, ['code', 'state']);

				if (!queryParams.code || !queryParams.state) {
					parseEmptyParamsErrorHandler();

					return null;
				}

				return { params: queryParams, loginType: LoginType.TinkoffID };
			}

			case telegramPath: {
				const { hashParams } = getValuesFromUrl(window.location.href, ['tgAuthResult']);
				const decodedParams = JSON.parse(atob(hashParams.tgAuthResult));

				return { params: decodedParams, loginType: LoginType.Telegram };
			}

			case vkPath: {
				const { queryParams } = getValuesFromUrl(window.location.href, ['code', 'state', 'device_id']);

				if (!queryParams.code || !queryParams.state || !queryParams.device_id) {
					parseEmptyParamsErrorHandler();

					return null;
				}

				return {
					params: {
						code: queryParams.code,
						state: queryParams.state,
						deviceId: queryParams.device_id
					},
					loginType: LoginType.Vkontakte
				};
			}

			case facebookPath: {
				const { queryParams } = getValuesFromUrl(window.location.href, ['code', 'state']);

				if (!queryParams.code || !queryParams.state) {
					parseEmptyParamsErrorHandler();

					return null;
				}

				return { params: queryParams, loginType: LoginType.Facebook };
			}

			case sberIdPath: {
				const { queryParams } = getValuesFromUrl(window.location.href, ['code', 'state']);
				const redirectUrl = getBackRedirectUrl(OauthServiceType.SberID, socialAuthCallbackUrl);

				if (!queryParams.code || !queryParams.state) {
					parseEmptyParamsErrorHandler();

					return null;
				}

				return {
					params: { ...queryParams, redirectUrl },
					loginType: LoginType.SberID
				};
			}

			case googlePath: {
				const { queryParams } = getValuesFromUrl(window.location.href, ['code', 'state']);

				if (!queryParams.code || !queryParams.state) {
					parseEmptyParamsErrorHandler();

					return null;
				}

				return { params: queryParams, loginType: LoginType.Google };
			}

			case alfaIdPath: {
				const { queryParams } = getValuesFromUrl(window.location.href, ['code', 'state']);

				if (!queryParams.code || !queryParams.state) {
					parseEmptyParamsErrorHandler();

					return null;
				}

				return { params: queryParams, loginType: LoginType.AlfaID };
			}

			default:
				return null;
		}
	} catch (error) {
		const providerName = getProviderName(locationPathname);
		parseErrorHandler(`Parse error in the ${providerName} callback url`);

		return null;
	}
};
